// Службові сторінки шелла: заглушка модуля, 404, помилка маршруту, редірект головної, публічний макет /p.
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, CalendarRange, Compass, Construction, LogIn } from 'lucide-react';
import { Navigate, Outlet, isRouteErrorResponse, useRouteError, useSearchParams } from 'react-router';
import { useAuth } from '@/lib/auth';
import { ButtonLink, Card, EmptyState, PageHeader } from '@/ui';
import { SEMESTER_PARAM, useSemester } from './semester';

export interface StubPageProps {
  /** Код модуля хвилі (U01…U17). */
  module: string;
  title: string;
  description?: string;
  icon?: LucideIcon;
}

/** Сторінка-заглушка розділу: модуль замінить маршрут між своїми маркерами в router.tsx. */
export function StubPage({ module, title, description, icon = Construction }: StubPageProps) {
  return (
    <>
      <PageHeader title={title} description={description} />
      <Card flush>
        <EmptyState
          icon={icon}
          title={`Модуль ${module} у розробці`}
          description="Розділ з'явиться в наступній збірці. Дані для нього вже можна готувати в довідниках."
          action={
            <ButtonLink to="/directories" size="sm">
              Перейти до довідників
            </ButtonLink>
          }
        />
      </Card>
    </>
  );
}

export function NotFoundPage() {
  const { search } = useSemester();
  return (
    <Card flush>
      <EmptyState
        icon={Compass}
        title="Сторінку не знайдено"
        description="Можливо, посилання застаріло або сторінку перенесено."
        action={
          <ButtonLink to={{ pathname: '/', search }} variant="primary" size="sm">
            На головну
          </ButtonLink>
        }
      />
    </Card>
  );
}

/** Помилка рендеру / завантаження маршруту (errorElement). */
export function RouteError() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const chunkFailed = error instanceof Error && /dynamically imported module|Failed to fetch/i.test(error.message);
  if (import.meta.env.DEV && error && !notFound) console.error(error);
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-4">
      <Card className="w-full max-w-lg" flush>
        <EmptyState
          tone={notFound ? 'neutral' : 'danger'}
          icon={notFound ? Compass : AlertTriangle}
          title={notFound ? 'Сторінку не знайдено' : chunkFailed ? 'Вийшла нова версія застосунку' : 'Щось пішло не так'}
          description={
            notFound
              ? 'Можливо, посилання застаріло.'
              : chunkFailed
                ? 'Оновіть сторінку, щоб завантажити актуальну версію.'
                : 'Сторінка не змогла відобразитися. Оновіть її або поверніться на головну.'
          }
          action={
            <>
              <ButtonLink to="/" variant="primary" size="sm" reloadDocument>
                На головну
              </ButtonLink>
            </>
          }
        />
      </Card>
    </div>
  );
}

/** Головна: персонал — календар розкладу, викладач/студент — «Мій розклад». */
export function HomeRedirect() {
  const { can } = useAuth();
  const { semester, search } = useSemester();
  const [sp] = useSearchParams();
  // Чекаємо, доки SemesterProvider запише семестр в URL: інакше його replace на «/» перебиває цей перехід.
  if (semester && sp.get(SEMESTER_PARAM) !== semester.code) return null;
  const to = !can('schedule.viewAll') && can('mySchedule.view') ? '/my' : '/schedule';
  return <Navigate to={{ pathname: to, search }} replace />;
}

/** Макет публічних сторінок /p (без входу, без шелла, D38). */
export function PublicLayout() {
  return (
    <div className="min-h-dvh bg-bg">
      <header className="flex h-14 items-center justify-between gap-3 border-b border-divider bg-surface px-4">
        <span className="flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-field bg-primary-solid text-on-primary-solid" aria-hidden>
            <CalendarRange className="size-[18px]" />
          </span>
          <span className="text-[15px] font-semibold">Розклад занять</span>
        </span>
        <ButtonLink to="/login" size="sm" variant="ghost" icon={LogIn}>
          Увійти
        </ButtonLink>
      </header>
      <main className="mx-auto w-full max-w-6xl px-3 py-6 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}

/** Заглушка публічного розкладу (модуль U14). */
export function PublicStubPage() {
  return (
    <>
      <PageHeader title="Розклад занять" description="Оберіть факультет, курс і групу або викладача." />
      <Card flush>
        <EmptyState icon={Construction} title="Модуль U14 у розробці" description="Публічний розклад з'явиться після публікації першої версії." />
      </Card>
    </>
  );
}
